/**
=========================================================
* STAIL - Unified Table Styles Utility
=========================================================
* Centralized table styling for consistency across all tables
* Applies to both DataTable components and raw MUI tables
* Created: December 2, 2025 - Phase 4B
=========================================================
*/

/**
 * UNIFIED TABLE STYLES
 * 
 * Design System:
 * - Header Background: Gold gradient (8% opacity)
 * - Header Text: Navy (#0a1628)
 * - Header Font: 10.4px, Bold (700), UPPERCASE
 * - Body Text: Dark gray (#344767) from theme
 * - Body Font: 10.4px, Regular (400)
 * - Row Hover: Gold tint (4% opacity)
 * 
 * Usage:
 * import { tableStyles } from 'utils/tableStyles';
 * 
 * For DataTable: Already applied automatically
 * For Raw MUI Tables: Apply tableStyles.container to TableContainer
 */

export const tableStyles = {
  // Container styles for raw MUI tables
  container: {
    '& .MuiTableCell-root': {
      padding: '4px 16px',
      fontSize: '10.4px',
      color: '#344767',
      borderBottom: '1px solid #f0f2f5',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      background: 'linear-gradient(135deg, rgba(244, 197, 66, 0.08) 0%, rgba(243, 156, 18, 0.08) 100%)',
      color: '#0a1628',
      fontSize: '10.4px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(244, 197, 66, 0.04) !important',
    },
  },

  // Header cell styles (for raw MUI TableCell in header)
  headerCell: {
    background: 'linear-gradient(135deg, rgba(244, 197, 66, 0.08) 0%, rgba(243, 156, 18, 0.08) 100%)',
    color: '#0a1628',
    fontSize: '10.4px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '4px 16px',
    borderBottom: '1px solid #f0f2f5',
  },

  // Body cell styles (for raw MUI TableCell in body)
  bodyCell: {
    color: '#344767',
    fontSize: '10.4px',
    fontWeight: 400,
    padding: '4px 16px',
    borderBottom: '1px solid #f0f2f5',
  },

  // Dense/compact variant
  dense: {
    '& .MuiTableCell-root': {
      padding: '2px 12px',
      fontSize: '9px',
      color: '#344767',
      borderBottom: '1px solid #f0f2f5',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      background: 'linear-gradient(135deg, rgba(244, 197, 66, 0.08) 0%, rgba(243, 156, 18, 0.08) 100%)',
      color: '#0a1628',
      fontSize: '9px',
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(244, 197, 66, 0.04) !important',
    },
  },

  // Large variant for better readability
  large: {
    '& .MuiTableCell-root': {
      padding: '8px 16px',
      fontSize: '12px',
      color: '#344767',
      borderBottom: '1px solid #f0f2f5',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      background: 'linear-gradient(135deg, rgba(244, 197, 66, 0.08) 0%, rgba(243, 156, 18, 0.08) 100%)',
      color: '#0a1628',
      fontSize: '12px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(244, 197, 66, 0.04) !important',
    },
  },
};

/**
 * Typography variants for table content
 * Use with MDTypography for consistent text styling
 */
export const tableTypography = {
  header: {
    variant: 'button',
    fontSize: '10.4px',
    fontWeight: 700,
    color: '#0a1628',
    textTransform: 'uppercase',
  },
  body: {
    variant: 'body2',
    fontSize: '10.4px',
    fontWeight: 400,
    color: '#344767',
  },
  bodyBold: {
    variant: 'body2',
    fontSize: '10.4px',
    fontWeight: 600,
    color: '#344767',
  },
};

export default tableStyles;
